import { createContext, useContext, useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useTranslation } from 'react-i18next';
import { authAPI, cgmAPI } from '../services/api';

const UserContext = createContext();

export function UserProvider({ children }) {
  const { keycloak, initialized } = useKeycloak();
  const { i18n } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [cgmCredentials, setCgmCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadUserData = async (force = false) => {
    // If we already have data and not forcing reload, skip
    if (userData && !force) {
      return userData;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.getMe();
      setUserData(response.data.user);
      setSubscription(response.data.subscription || null);
      return response.data.user;
    } catch (err) {
      console.error('Error loading user data:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loadCgmCredentials = async (force = false) => {
    // If we already have data and not forcing reload, skip
    if (cgmCredentials && !force) {
      console.log('[UserContext] Using cached CGM credentials:', cgmCredentials);
      return cgmCredentials;
    }

    try {
      console.log('[UserContext] Fetching CGM credentials from API...');
      const response = await cgmAPI.getCredentials();
      console.log('[UserContext] CGM credentials response:', response);
      console.log('[UserContext] CGM credentials data:', response.data);
      const credentials = response.data.credentials || [];
      console.log('[UserContext] Setting CGM credentials:', credentials);
      setCgmCredentials(credentials);
      return credentials;
    } catch (err) {
      console.error('[UserContext] Error loading CGM credentials:', err);
      console.error('[UserContext] Error response:', err.response);
      console.error('[UserContext] Error response data:', err.response?.data);
      throw err;
    }
  };

  const updateUserData = (updates) => {
    setUserData(prev => ({ ...prev, ...updates }));
  };

  const updateCgmCredentials = (credentials) => {
    setCgmCredentials(credentials);
  };

  const refreshUserData = () => {
    return loadUserData(true);
  };

  const refreshCgmCredentials = () => {
    return loadCgmCredentials(true);
  };

  useEffect(() => {
    // Only load user data if Keycloak is initialized and user is authenticated
    if (initialized && keycloak.authenticated) {
      loadUserData();
    } else if (initialized) {
      // If initialized but not authenticated, stop loading
      setLoading(false);
    }
  }, [initialized, keycloak.authenticated]);

  // Sync i18n language when userData changes
  useEffect(() => {
    if (userData && userData.language) {
      if (i18n.language !== userData.language) {
        i18n.changeLanguage(userData.language);
      }
    }
  }, [userData, i18n]);

  // Computed subscription values for easy access
  const isPremium = subscription?.isPremium || false;
  const subscriptionStatus = subscription?.status || 'free';

  const value = {
    userData,
    subscription,
    isPremium,
    subscriptionStatus,
    cgmCredentials,
    loading,
    error,
    loadUserData,
    loadCgmCredentials,
    updateUserData,
    updateCgmCredentials,
    refreshUserData,
    refreshCgmCredentials,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
